import React, { createContext, Suspense, useContext } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import LoadingContainer from '../components/LoadingContainer';
import shopApi from './shop';

const store = createContext({});
const { Provider } = store;

export const getShopData = async () => {
	const shops = await shopApi.getMyShops();

	return shops.map((shop) => ({
		...shop,
		inSetup: !shop.googleMerchantId && !shop.stockInventorySource?.latestIngest
	}));
};

const Shop = ({ children }) => {
	const { shopId: pathId } = useParams();
	const swr = useSWR('active-shops', () => getShopData(), {
		suspense: true,
		revalidateOnFocus: false,
		refreshWhenHidden: false
	});
	const shop = swr.data.find((shop) => shop.id === pathId) || {};
	const organisation = swr.data
		? { id: swr.data[0].organisationId, name: swr.data[0].organisation, locationCount: swr.data.length }
		: null;
	const updateShop = async (id, update) => {
		try {
			await swr.mutate(async (data) => {
				const unchangedShops = data.filter((shop) => shop.id !== id);
				const updatedShop = await shopApi.updateMyShop(id, update);
				return [...unchangedShops, updatedShop];
			}, false);
		} catch (e) {
			alert("Something went wrong and we couldn't update your details.\nPlease try again.");
		}
	};

	return <Provider value={{ shop, organisation, ...swr, updateShop }}>{children}</Provider>;
};

// defaultShop is for test cases ONLY
const ShopProvider = ({ children, defaultShop = undefined }) => {
	if (defaultShop) {
		return <Provider value={{ shop: defaultShop }}>{children}</Provider>;
	}

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Shop>{children}</Shop>
		</Suspense>
	);
};

/**
 * @typedef {import('swr').MutatorCallback} SWRMutator
 * @typedef {import('@nearst/shop-data/types').Shop} Shop
 * @typedef {Object} Organisation
 * @typedef {string} Organisation.id
 * @typedef {string} Organisation.name
 * @typedef {number} Organisation.locationCount
 * @typedef {Object} Shop.availableChannels
 * @typedef {Object} Return
 * @property {boolean} Return.isValidating
 * @property {boolean} Return.isLoading
 * @property {SWRMutator} Return.mutate
 * @property {Shop[]} Return.data
 * @property {Shop} Return.shop
 * @property {Organisation} Return.organisation
 * @property {(id: string, update: object) => Promise<void>} Return.updateShop
 */

/**
 * Update and revalidate the shop data
 *
 * @async
 * @typedef {function} updateShop
 * @param {string} id The id of the shop to update
 * @param {Partial<Shop>} shop The shop update
 * @return {Promise<Shop[]>}
 */

/**
 * Retrieves shops.
 * @returns {Partial<Return>}
 */
const useShop = () => useContext(store);

export { useShop, ShopProvider, Provider as TestOnlyProvider };
